import React, { useEffect, useId, useState } from 'react';
import { ecosystemRequest } from '../../services/api/passportEcosystem';

export type StudioChoice = { value: string; label: string; detail?: string };
export function StudioLookup({
  label,
  kind = 'subject',
  value,
  onChange,
  help,
  disabled = false,
}: {
  label: string;
  kind?: string;
  value: string;
  onChange: (value: string, choice?: StudioChoice) => void;
  help?: string;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<StudioChoice[]>([]);
  const [selected, setSelected] = useState<StudioChoice>();
  const [state, setState] = useState('');
  const [retry, setRetry] = useState(0);
  const id = useId();
  useEffect(() => {
    let active = true;
    setState('Searching…');
    const timer = setTimeout(
      () =>
        void ecosystemRequest<StudioChoice[]>(
          'choices',
          undefined,
          { kind, q: query, selected: value },
          'admin'
        )
          .then((result) => {
            if (!active) return;
            setRows(result);
            setSelected(result.find((r) => r.value === value));
            setState(
              result.length
                ? ''
                : 'No matches. Try another name or create the item in its Studio section first.'
            );
          })
          .catch(() => {
            if (active) setState('Choices could not load. Retry the search.');
          }),
      query ? 250 : 0
    );
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [kind, query, value, retry]);
  return (
    <div className="ps-field ps-lookup">
      <label htmlFor={id}>{label}</label>
      {help && (
        <p id={`${id}-help`} className="ps-help">
          {help}
        </p>
      )}
      <input
        aria-label={`Search ${label.toLowerCase()}`}
        aria-describedby={help ? `${id}-help` : undefined}
        placeholder="Search by name…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
      />
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const row = rows.find((r) => r.value === e.target.value);
          setSelected(row);
          onChange(e.target.value, row);
        }}
      >
        <option value="">Choose {label.toLowerCase()}</option>
        {value && !rows.some((r) => r.value === value) && (
          <option value={value}>{selected?.label || 'Current selection (search to change)'}</option>
        )}
        {rows.map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
            {r.detail ? ` · ${r.detail}` : ''}
          </option>
        ))}
      </select>
      {state && (
        <p className="ps-help" role="status">
          {state}
        </p>
      )}
      {state.includes('Retry') && (
        <button type="button" className="pc-link" onClick={() => setRetry((n) => n + 1)}>
          Retry choices
        </button>
      )}
    </div>
  );
}
export function StudioTargets({
  value,
  onChange,
  kind = 'subject',
  label = 'Places, builds or stops',
  help,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  kind?: string;
  label?: string;
  help?: string;
}) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let active = true;
    void Promise.allSettled(
      value.map((selected) =>
        ecosystemRequest<StudioChoice[]>('choices', undefined, { kind, selected }, 'admin').then(
          (rows) =>
            [selected, rows.find((r) => r.value === selected)?.label || 'Saved item'] as const
        )
      )
    )
      .then((pairs) => {
        if (active)
          setNames(
            Object.fromEntries(
              pairs.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))
            )
          );
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [value.join('|'), kind]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="ps-targets">
      <StudioLookup
        label={label}
        kind={kind}
        value=""
        help={help}
        onChange={(v, row) => {
          if (v && !value.includes(v)) {
            setNames((n) => ({ ...n, [v]: row?.label || 'Selected item' }));
            onChange([...value, v]);
          }
        }}
      />
      <ol>
        {value.map((v, i) => (
          <li key={v}>
            <span>{names[v] || 'Loading item…'}</span>
            <button
              type="button"
              className="pc-link"
              aria-label={`Move ${names[v] || 'item'} earlier`}
              disabled={i === 0}
              onClick={() => {
                const next = [...value];
                [next[i - 1], next[i]] = [next[i], next[i - 1]];
                onChange(next);
              }}
            >
              ↑
            </button>
            <button
              type="button"
              className="pc-link"
              aria-label={`Remove ${names[v] || 'item'}`}
              onClick={() => onChange(value.filter((x) => x !== v))}
            >
              Remove
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
export function StudioTimezone({
  value,
  onChange,
  help,
}: {
  value: string;
  onChange: (v: string) => void;
  help?: string;
}) {
  const id = useId();
  const zones = (
    Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf?.('timeZone') || [
    'America/Denver',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Rome',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];
  return (
    <label className="ps-field">
      Local timezone
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={id}>
        {Array.from(new Set(['UTC', value, ...zones])).map((z) => (
          <option key={z} value={z}>
            {z.replaceAll('_', ' ')}
          </option>
        ))}
      </select>
      <span id={id} className="ps-help">
        {help ||
          'Choose the place’s local timezone. It controls visit days and daylight-saving changes.'}
      </span>
      <button
        type="button"
        className="pc-link"
        onClick={() => onChange(Intl.DateTimeFormat().resolvedOptions().timeZone)}
      >
        Use my device’s timezone
      </button>
    </label>
  );
}
export function localMinute(instant: string, zone: string): string {
  if (!instant || !Number.isFinite(Date.parse(instant))) return '';
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date(instant))
        .map((p) => [p.type, p.value])
    );
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch {
    return '';
  }
}
/** Resolve all real instants, including both sides of a DST overlap. Never shift a nonexistent time. */
export function localInstants(local: string, zone: string): string[] {
  const wall = Date.parse(`${local}Z`);
  if (!Number.isFinite(wall)) return [];
  const offsets = new Set<number>();
  for (let h = -36; h <= 36; h += 6) {
    const t = wall + h * 3600000;
    const formatted = localMinute(new Date(t).toISOString(), zone);
    if (formatted) offsets.add(Date.parse(`${formatted}Z`) - t);
  }
  return Array.from(offsets)
    .map((offset) => new Date(wall - offset).toISOString().replace('.000Z', 'Z'))
    .filter((t) => localMinute(t, zone) === local)
    .sort();
}
export function StudioInstant({
  label,
  value,
  onChange,
  timezone = 'UTC',
  help,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  timezone?: string;
  help?: string;
}) {
  const normalized =
    value && !/[Z]|[+-]\d\d:\d\d$/.test(value) ? value.replace(' ', 'T') + 'Z' : value;
  const [local, setLocal] = useState(localMinute(normalized || '', timezone));
  const [candidates, setCandidates] = useState<string[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (value !== 'unresolved-local-time') {
      setLocal(localMinute(normalized || '', timezone));
      setError('');
      setCandidates([]);
    }
  }, [normalized, timezone, value]);
  return (
    <label className="ps-field">
      {label}
      <input
        type="datetime-local"
        value={local}
        onChange={(e) => {
          const next = e.target.value;
          setLocal(next);
          setError('');
          setCandidates([]);
          if (!next) {
            onChange(null);
            return;
          }
          const matches = localInstants(next, timezone);
          if (matches.length === 1) onChange(matches[0]);
          else {
            onChange('unresolved-local-time');
            setCandidates(matches);
            setError(
              matches.length
                ? 'This time happens twice when clocks go back. Choose the intended occurrence.'
                : 'This local time does not exist when clocks go forward. Choose another time.'
            );
          }
        }}
      />
      <span className="ps-help">
        {help || 'Leave blank for no date limit.'} Times shown in {timezone.replaceAll('_', ' ')}.
      </span>
      {error && (
        <span role="alert" className="pc-error">
          {error}
        </span>
      )}
      {candidates.map((candidate, i) => (
        <button
          type="button"
          className="pc-link"
          key={candidate}
          onClick={() => {
            onChange(candidate);
            setError('');
            setCandidates([]);
          }}
        >
          {i === 0 ? 'First occurrence' : 'Second occurrence'} · {candidate.slice(11, 16)} UTC
        </button>
      ))}
    </label>
  );
}
